import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import {
  SectionHeading, EmptySection, Field, BoolBadge, TagList, formatTimestamp,
} from './shared';
import PhotoGrid from './PhotoGrid';
import type { PhotoAttachmentWithUrl } from '@/services/db/dsrDetail';

interface Props {
  data: any[];
  photos?: PhotoAttachmentWithUrl[];
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  near_miss: 'bg-mozaic-orange/15 text-mozaic-orange',
  first_aid: 'bg-mozaic-blue/15 text-mozaic-blue',
  recordable_injury: 'bg-mozaic-red/15 text-mozaic-red',
  lost_time_injury: 'bg-mozaic-red/20 text-mozaic-red font-bold',
  property_damage: 'bg-mozaic-orange/15 text-mozaic-orange',
  environmental_release: 'bg-mozaic-teal/15 text-mozaic-teal',
  security_incident: 'bg-mozaic-navy/15 text-mozaic-navy',
};

export default function SafetyEventsSection({ data, photos = [] }: Props) {
  return (
    <section>
      <SectionHeading id="section-safety-events" title="Safety Events" count={data.length} />
      <div className="mt-2 mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p>
          Safety event records may be subject to regulatory reporting requirements.
          Ensure all information is accurate and complete before submission. Consult
          your safety manager for incidents requiring regulatory notification.
        </p>
      </div>
      {data.length === 0 ? (
        <EmptySection name="safety events" />
      ) : (
        <div className="mt-2 space-y-3">
          {data.map((row: any) => (
            <Card key={row.id}>
              <CardContent className="pt-4">
                <div className="mb-2 flex items-center gap-2">
                  <Badge className={EVENT_TYPE_COLORS[row.event_type] ?? 'bg-muted text-muted-foreground'}>
                    {(row.event_type ?? '').replace(/_/g, ' ')}
                  </Badge>
                  {row.is_work_stopped && (
                    <Badge className="bg-mozaic-red/15 text-mozaic-red">Work Stopped</Badge>
                  )}
                </div>
                <p className="mb-1 text-sm font-medium">{row.event_title}</p>
                <p className="mb-3 text-sm text-muted-foreground">{row.event_description}</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Field label="Date/Time" value={formatTimestamp(row.event_datetime)} />
                  <Field label="Location" value={row.location_area} />
                  <Field label="Personnel Involved" value={row.personnel_count} />
                  <div className="col-span-full">
                    <Field label="Immediate Actions" value={row.immediate_actions} />
                  </div>
                  <div className="col-span-full">
                    <Field label="Categories" value={<TagList tags={row.category} />} />
                  </div>
                  <Field label="Supervisor Notified" value={<BoolBadge value={row.is_supervisor_notified} />} />
                  <Field label="Management Notified" value={<BoolBadge value={row.is_management_notified} />} />
                  <Field label="Regulatory Required" value={<BoolBadge value={row.is_regulatory_required} />} />
                  <Field label="Contractor Crew" value={row.contractor_crew} />
                  {/* Near miss fields */}
                  {row.near_miss_potential_severity && (
                    <Field label="Potential Severity" value={row.near_miss_potential_severity} />
                  )}
                  {row.near_miss_conditions && (
                    <div className="col-span-full">
                      <Field label="Near Miss Conditions" value={row.near_miss_conditions} />
                    </div>
                  )}
                  {/* Injury fields */}
                  {row.injured_person_role && <Field label="Injured Person Role" value={row.injured_person_role} />}
                  {row.injury_type && <Field label="Injury Type" value={row.injury_type} />}
                  {row.body_part_affected && <Field label="Body Part Affected" value={row.body_part_affected} />}
                  {row.treatment_provided && <Field label="Treatment Provided" value={row.treatment_provided} />}
                </div>
                <PhotoGrid photos={photos} entityId={row.id} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
